import classNames from "classnames";
import { action, computed, IComputedValue, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import {
  ActionType,
  ImportModule,
  Pane,
  PaneType,
  PaneViewMode,
} from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { assert } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  globalValuesMobx,
  isSafeModeAtom,
} from "./CanvasState";
import { getTypeModuleNameForImport } from "./CodeAnalysis";
import { codeWorkerCall } from "./ExpressionEditor";
import { JSONRenderRoot } from "./JSONRender";
import { PaneBody } from "./PaneBody";
import { PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";
import { PinIcon } from "./SvgIcons";

const NPM_MODULE_NAMES: Record<string, string> = {
  "lodash-es": "lodash",
  react: "React",
  "react-dom": "ReactDOM",
};
function getPaneNameForModuleIdentifier(moduleId: string) {
  const match = /^([a-zA-Z0-9_\-]+)(@[\d\.]+)?$/.exec(moduleId);
  if (match !== null) {
    const customName = NPM_MODULE_NAMES[match[1]];
    if (customName !== undefined) {
      return customName;
    }
    if (!match[1].includes("-")) {
      return match[1];
    }
  }
  return undefined;
}

function PaneImportModuleInput({
  paneId,
  paneModule,
  focusOnMount,
  setMostRecentNpmImport,
  onClose,
}: {
  paneId: string;
  paneModule: ImportModule | undefined;
  focusOnMount: boolean;
  setMostRecentNpmImport: (moduleId: string) => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [importType, setImportType] = useState<"npm" | "url" | "script">(
    () => paneModule?.[0] ?? "npm"
  );
  const [value, setValue] = useState(() => paneModule?.[1] ?? "");
  const hasChange =
    paneModule === undefined ||
    paneModule[0] !== importType ||
    paneModule[1] !== value;
  useEffect(() => {
    if (focusOnMount) {
      inputRef.current!.focus();
    }
  }, []);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (hasChange) {
          canvasDispatch([
            ActionType.SetPaneModule,
            {
              paneId,
              module: [importType, value],
            },
          ]);
          if (importType === "npm") {
            const autoPaneName = getPaneNameForModuleIdentifier(value);
            if (autoPaneName !== undefined) {
              canvasDispatch([
                ActionType.SetPaneName,
                { paneId, name: autoPaneName },
              ]);
            }
            setMostRecentNpmImport(value);
          }
        }
        onClose();
      }}
      className="h-full"
    >
      <div className="flex h-full bg-neutral-50 pr-2">
        <select
          value={importType}
          onChange={(e) => {
            setImportType(e.target.value as any);
          }}
          className="w-16 bg-neutral-0 pl-1 border border-transparent focus:border-neutral-200 outline-none transition"
        >
          <option value="npm">npm</option>
          <option value="url">url</option>
          <option value="script">script</option>
        </select>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          placeholder={
            importType === "npm"
              ? "eg lodash-es, uuid@8.3"
              : importType === "url"
              ? "url to import"
              : importType === "script"
              ? "script url to run"
              : ""
          }
          className="grow bg-transparent outline-none pl-2 focus-visible-inset"
          ref={inputRef}
        />
        <button
          disabled={value === "" || !hasChange}
          className="px-1 py-0.5 self-center rounded-sm bg-primary-400 hover:bg-primary-500 text-neutral-0 disabled:bg-neutral-200 ml-2"
        >
          update
        </button>
        {paneModule !== undefined ? (
          <button
            type="button"
            onClick={() => {
              onClose();
            }}
            className="text-neutral-400 hover:text-neutral-600 transition ml-2"
          >
            cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

function PinModuleUrlButton({
  paneId,
  module,
  pinnedUrl,
}: {
  paneId: string;
  module: [type: "npm", moduleId: string, pinUrl?: string];
  pinnedUrl: string | undefined;
}) {
  return (
    <Tooltip
      content={
        module[2] !== undefined ? (
          <div>
            <div>click to unpin</div>
            <div className="whitespace-normal break-all opacity-50">
              {module[2]}
            </div>
          </div>
        ) : (
          "pin this dependency\nfor faster loads"
        )
      }
    >
      <button
        onClick={async () => {
          if (module[2] === undefined) {
            canvasDispatch([
              ActionType.SetPaneModule,
              {
                paneId,
                module: ["npm", module[1], pinnedUrl!],
              },
            ]);
          } else {
            canvasDispatch([
              ActionType.SetPaneModule,
              {
                paneId,
                module: ["npm", module[1]],
              },
            ]);
          }
        }}
        className={classNames(
          "flex shrink-0 p-1 mr-0.5 transition",
          module[2] !== undefined
            ? "text-primary-400 hover:text-primary-600"
            : "text-neutral-400 hover:text-neutral-600"
        )}
      >
        <PinIcon />
      </button>
    </Tooltip>
  );
}

const importReactDomVersionComputed = computed(() => {
  for (const pane of canvasAtom.value.panes) {
    if (pane.type !== PaneType.Import || pane.module?.[0] !== "npm") {
      continue;
    }
    const moduleId = pane.module[1];
    const matches = moduleId.match(/^react-dom@([\d\.]+)$/);
    if (matches === null) {
      continue;
    }
    return matches[1];
  }
  return undefined;
});

export const PaneImportComponent = observer(
  ({
    paneId,
    paneComputed,
    isMaximized,
    focusOnMount,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Import }> | undefined
    >;
    isMaximized: boolean;
    focusOnMount: boolean;
  }) => {
    const [loadedModule, setLoadedModule] = useState<
      [moduleName: string, moduleValue: any] | undefined
    >(undefined);
    const activePaneState = activePaneStates.get(paneId)!;
    assert(activePaneState.type === PaneType.Import);
    const outputBox = activePaneState.outputBoxes[0];
    const output = outputBox.get();
    const [isEditingImport, setIsEditingImport] = useState(false);
    const paneModule = computed(() => paneComputed.get()?.module).get();
    const paneUseDefault = computed(
      () => paneComputed.get()?.useDefault ?? false
    ).get();
    const paneModuleKey = paneModule?.join("-");
    const [pinnedUrls, setPinnedUrls] = useState<Record<string, string>>({});
    const [mostRecentNpmImport, setMostRecentNpmImport] = useState<
      string | undefined
    >(undefined);
    useEffect(() => {
      if (
        mostRecentNpmImport !== undefined &&
        paneModule?.[0] === "npm" &&
        paneModule[1] === mostRecentNpmImport
      ) {
        return () => {
          setMostRecentNpmImport(undefined);
        };
      }
    }, [mostRecentNpmImport, paneModule]);

    const isSafeMode = isSafeModeAtom.value;
    const importReactDomVersion = importReactDomVersionComputed.get();
    useEffect(() => {
      if (isSafeMode) {
        return;
      }
      let scriptElement: HTMLScriptElement | undefined;
      async function go() {
        if (paneModule !== undefined) {
          if (paneModule[0] === "script") {
            scriptElement = document.createElement("script");
            const scriptUrl = paneModule[1];
            scriptElement.onload = () => {
              setLoadedModule([scriptUrl, {}]);
            };
            scriptElement.onerror = action(() => {
              setLoadedModule([scriptUrl, undefined]);
              outputBox.set(["error", undefined]);
            });
            scriptElement.src = scriptUrl;
            document.head.appendChild(scriptElement);
          } else {
            const moduleId = paneModule[1];
            function getNpmModuleUrl() {
              const questionMarkIndex = moduleId.indexOf("?");
              const urlSearchParams = new URLSearchParams(
                questionMarkIndex >= 0
                  ? moduleId.substring(questionMarkIndex)
                  : ""
              );
              if (importReactDomVersion !== undefined) {
                const prevDeps = urlSearchParams.get("deps");
                let depsValue = `react@${importReactDomVersion}`;
                if (prevDeps !== null) {
                  depsValue = `${prevDeps},${depsValue}`;
                }
                urlSearchParams.set("deps", depsValue);
              }
              // urlSearchParams.set("pin", ESM_SH_PIN_VERSION);
              return `https://esm.sh/${
                questionMarkIndex >= 0
                  ? moduleId.substring(0, questionMarkIndex)
                  : moduleId
              }?${urlSearchParams.toString()}`;
            }
            const moduleUrl =
              paneModule[0] === "npm"
                ? paneModule[2] ?? getNpmModuleUrl()
                : paneModule[0] === "url"
                ? paneModule[1]
                : paneModule[1];
            try {
              const module = await import(/* @vite-ignore */ moduleUrl);
              setLoadedModule([moduleId, module]);
              if (paneModule[0] === "npm") {
                try {
                  const moduleHeaders = await fetch(
                    `https://esm.sh/${moduleId}`,
                    { method: "GET" }
                  ).then((r) => r.headers);
                  const typescriptTypePath =
                    moduleHeaders.get("x-typescript-types");
                  if (typescriptTypePath !== null) {
                    const lastSlashIndex = typescriptTypePath.lastIndexOf("/");
                    const lastSegment = typescriptTypePath.slice(
                      lastSlashIndex + 1
                    );
                    codeWorkerCall({
                      type: "LoadTypedef",
                      typeModuleName: getTypeModuleNameForImport(paneModule),
                      cdnTypeRoot: `${typescriptTypePath.slice(
                        0,
                        lastSlashIndex + 1
                      )}`,
                      filePath:
                        lastSegment.indexOf(".d.ts") === -1
                          ? `${lastSegment}.d.ts`
                          : lastSegment,
                    });
                  }
                  // const skypackPinnedUrl = moduleHeaders.get("x-pinned-url");
                  // if (skypackPinnedUrl !== null) {
                  //   setPinnedUrls((pinnedUrls) => ({
                  //     ...pinnedUrls,
                  //     [moduleId]: `https://cdn.skypack.dev${skypackPinnedUrl}`,
                  //   }));
                  // }
                } catch {}
              }
            } catch (e) {
              setLoadedModule([moduleId, undefined]);
              runInAction(() => {
                outputBox.set(["error", e]);
              });
            }
          }
        }
      }
      go();
      return () => {
        if (scriptElement !== undefined) {
          scriptElement.parentElement?.removeChild(scriptElement);
        }
      };
    }, [paneModuleKey, importReactDomVersion, isSafeMode]);
    useEffect(
      action(() => {
        if (
          paneModule !== undefined &&
          loadedModule !== undefined &&
          loadedModule[1] !== undefined &&
          paneModule[1] === loadedModule[0]
        ) {
          outputBox.set([
            "value",
            paneUseDefault ? loadedModule[1].default : loadedModule[1],
          ]);
        }
      }),
      [paneModuleKey, loadedModule, paneUseDefault]
    );
    const paneIsGlobal = computed(
      () => paneComputed.get()?.isGlobal ?? false
    ).get();
    const outputGlobalName = paneIsGlobal
      ? computed(() => paneComputed.get()?.name).get()
      : undefined;
    const [didToggleToGlobal, setDidToggleToGlobal] = useState(false);
    useEffect(
      action(() => {
        if (outputGlobalName !== undefined) {
          if (output[0] === "value") {
            globalValuesMobx[outputGlobalName] = output[1];
          } else {
            delete globalValuesMobx[outputGlobalName];
          }
        }
      }),
      [outputGlobalName, output]
    );
    const viewMode = activePaneState.viewMode.get();

    return (
      <>
        <PaneHeaderBar
          paneId={paneId}
          paneComputed={paneComputed}
          isMaximized={isMaximized}
        >
          {viewMode === PaneViewMode.Normal ? (
            <>
              <Tooltip content={"use default export"}>
                <button
                  onClick={(e) => {
                    canvasDispatch([
                      ActionType.SetPaneUseDefault,
                      {
                        paneId,
                        useDefault: !paneUseDefault,
                      },
                    ]);
                  }}
                  className={classNames(
                    "px-1 py-0.5 flex items-center gap-1 hover:bg-neutral-400 hover:text-neutral-0 transition rounded-sm ml-2 first:ml-0",
                    paneUseDefault ? "text-primary-400" : "text-neutral-400"
                  )}
                >
                  <span>default</span>
                  <FeatherIcon
                    icon={paneUseDefault ? "check" : "arrow-up-right"}
                    size="xs"
                  />
                </button>
              </Tooltip>
              {paneIsGlobal ? (
                <Tooltip content={"define var name\nas global"}>
                  <label
                    className={classNames("flex items-center mx-1", {
                      "opacity-50": !paneIsGlobal,
                    })}
                  >
                    <span className="text-neutral-500 mr-1">global</span>
                    <input
                      type="checkbox"
                      checked={paneIsGlobal}
                      onChange={(e) => {
                        const isGlobal = e.target.checked;
                        setDidToggleToGlobal(true);
                        canvasDispatch([
                          ActionType.SetPaneIsGlobal,
                          { paneId, isGlobal },
                        ]);
                      }}
                    />
                  </label>
                </Tooltip>
              ) : null}
            </>
          ) : null}
          <div className="grow" />
          <PaneTransformButton paneId={paneId} />
        </PaneHeaderBar>
        <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
          <div className="h-full flex flex-col relative">
            <div className="h-8 shrink-0 border-b border-neutral-100">
              {isEditingImport || paneModule === undefined ? (
                <PaneImportModuleInput
                  paneId={paneId}
                  paneModule={paneModule}
                  setMostRecentNpmImport={setMostRecentNpmImport}
                  onClose={() => setIsEditingImport(false)}
                  focusOnMount={focusOnMount || isEditingImport}
                />
              ) : (
                <div className="flex items-center h-full overflow-hidden px-2">
                  <div className="text-neutral-400 pr-1">{paneModule[0]}</div>
                  <div className="grow overflow-hidden break-all line-clamp-2 text-ellipsis max-h-8 px-2 select-text">
                    {paneModule[1]}
                  </div>
                  {paneModule !== undefined &&
                  paneModule[0] === "npm" &&
                  (paneModule[2] !== undefined ||
                    pinnedUrls[paneModule[1]] !== undefined) ? (
                    <PinModuleUrlButton
                      paneId={paneId}
                      module={paneModule}
                      pinnedUrl={pinnedUrls[paneModule[1]]}
                    />
                  ) : null}
                  <button
                    onClick={() => {
                      setIsEditingImport(true);
                    }}
                    className="flex shrink-0 text-neutral-400 hover:text-neutral-600 p-1 transition"
                  >
                    <FeatherIcon icon="edit" size="sm" />
                  </button>
                </div>
              )}
            </div>
            <div
              className={classNames(
                "h-full overflow-auto transition select-text",
                {
                  "opacity-50":
                    paneModule !== undefined &&
                    (loadedModule === undefined ||
                      paneModule[1] !== loadedModule[0]),
                }
              )}
            >
              {output[0] === "value" ? (
                <div className="pt-2 pb-[7px] px-2">
                  <JSONRenderRoot
                    paneId={paneId}
                    value={output[1]}
                    withoutContextMenu={true}
                  />
                </div>
              ) : paneModule !== undefined && output[0] === "waiting" ? (
                <div className="font-mono-natto p-2 text-neutral-400">
                  loading...
                </div>
              ) : output[0] === "error" ? (
                <div>
                  <div className="font-mono-natto p-2 text-red-400">
                    {output[1] !== undefined ? output[1].toString() : "error"}
                  </div>
                  {paneModule?.[0] === "npm" ? (
                    <div className="p-2 text-neutral-400">
                      <div className="mb-2">
                        natto uses{" "}
                        <a
                          href="https://esm.sh/"
                          target="_blank"
                          rel="noreferer"
                          className="text-primary-500 hover:underline"
                        >
                          esm.sh
                        </a>{" "}
                        for npm imports
                      </div>
                      <button
                        onClick={() => {
                          canvasDispatch([
                            ActionType.SetPaneModule,
                            {
                              paneId,
                              module: [
                                "url",
                                `https://jspm.dev/${paneModule?.[1]}`,
                              ],
                            },
                          ]);
                        }}
                        className="button"
                      >
                        try importing from jspm.dev
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div></div>
              )}
            </div>
            {mostRecentNpmImport === "lodash" &&
            paneModule?.[0] === "npm" &&
            paneModule[1] === mostRecentNpmImport ? (
              <div className="absolute bg-primary-100 bottom-2 right-2 text-neutral-500 p-1 rounded-sm flex items-center gap-1">
                <div>
                  You likely want to use{" "}
                  <button
                    onClick={() => {
                      canvasDispatch([
                        ActionType.SetPaneModule,
                        {
                          paneId,
                          module: ["npm", "lodash-es"],
                        },
                      ]);
                    }}
                    className="font-medium text-primary-500 hover:text-primary-600 transition"
                  >
                    lodash-es
                  </button>{" "}
                  instead
                </div>
                <button
                  onClick={() => {
                    setMostRecentNpmImport(undefined);
                  }}
                >
                  <FeatherIcon
                    icon="x"
                    size="sm"
                    className="flex shrink-0 text-neutral-400 hover:text-neutral-500 transition"
                  />
                </button>
              </div>
            ) : null}
          </div>
        </PaneBody>
      </>
    );
  }
);
